import { Box, Typography } from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";

import { useRedfidViews } from "../../utils/redfidView";
import { tokens } from "../../theme";
import TargetDesc from "../../components/targets/TargetDesc";
import TargetMet from "../../components/targets/TargetMet";
import LoadingState from "../../components/LoadingState";

import { useAuth } from "../../AuthContext";
import PageGuard from "../../components/PageGuard";

import { LEVEL_DESCRIPTION } from "../../utils/mapers";
import {
  getCount,
  getList,
  normalizeText,
  sortNames,
  uniqueSorted,
} from "../../utils/utils";

const Formacion = () => {
  const auth = useAuth();
  const colors = tokens();
  const { loading, error, data } = useRedfidViews();

  if (loading) return <LoadingState message="" />;
  if (error) return <div>Error cargando datos: {String(error)}</div>;

  const niveles = data?.formacion?.niveles ?? [];
  const grado = data?.formacion?.grados ?? [];
  const titulo = data?.formacion?.titulos ?? [];
  const programa = data?.formacion?.programas ?? [];



  const canSeeList = !!auth?.user?.is_admin || !!auth?.user?.is_staff;

  const matchPrograma = (...ids) => {
    const normalizedIds = ids.map(normalizeText);
    return (x) => normalizedIds.includes(normalizeText(x?.id));
  };

  const getParticipantName = (p) => {
    const fullName =
      p?.nombre_completo ??
      p?.nombreCompleto ??
      p?.name ??
      p?.["Indique su nombre y apellido"] ??
      `${p?.nombre ?? ""} ${p?.apellido ?? ""}`;

    return String(fullName ?? "").trim();
  };

  const getParticipantUniversity = (p) =>
    String(
      p?.universidad_full ??
      p?.nombre_universidad ??
      p?.universidad ??
      p?.Universidad ??
      "Sin información"
    ).trim();

  const getProgramaFormadores = (idOrPredicate) =>
    uniqueSorted(getList(programa, idOrPredicate));

  const getUniversidadesFrecuencia = (formadores = []) => {
    const namesSet = new Set((formadores ?? []).map(normalizeText));
    const counts = {};

    (data?.tables?.participantes ?? []).forEach((p) => {
      const participantName = normalizeText(getParticipantName(p));

      if (!namesSet.has(participantName)) return;

      const universidad = getParticipantUniversity(p);

      if (
        !universidad ||
        universidad === "—" ||
        universidad === "-" ||
        normalizeText(universidad) === "sin informacion"
      ) {
        return;
      }

      counts[universidad] = (counts[universidad] ?? 0) + 1;
    });

    return Object.entries(counts)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) =>
        String(a.label).localeCompare(String(b.label), "es", {
          sensitivity: "base",
        })
      );
  };

  const renderName = (name) => (
    <Box
      key={name}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      borderBottom={`1px solid ${colors.primary[300]}`}
      pb="8px"
    >
      <Typography
        color={colors.primary[100]}
        fontWeight={900}
        sx={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={name}
      >
        {name}
      </Typography>
    </Box>
  );

  const nivelOrder = { novel: 0, intermedio: 1, experto: 2 };
  niveles.sort((a, b) => (nivelOrder[a.id] ?? 999) - (nivelOrder[b.id] ?? 999));

  const commonCardProps = {
    variant: "dash",
    fullWidth: true,
    titleWrap: true,
    icon: <GroupsIcon sx={{ color: colors.green[200] }} />,
    bgColor: colors.primary[200],
    sx: { minHeight: 84, borderRadius: "18px" },
    valueLabel: {
      singular: "persona",
      plural: "personas",
    },
    renderItem: canSeeList ? (item) => renderName(String(item)) : undefined,
    canViewList: canSeeList,
    hideListWhenEmpty: true,
  };

  const programCardProps = {
    variant: "dash",
    fullWidth: true,
    titleWrap: true,
    icon: <GroupsIcon sx={{ color: colors.green[200] }} />,
    bgColor: colors.primary[200],
    sx: { minHeight: 84, borderRadius: "18px" },
    valueLabel: {
      singular: "formador/a",
      plural: "formadores/as",
    },
    columns: 2,
    detailMode: "collapse",
    disableParticipantsDetail: !canSeeList,
    participantsStatKey: "formadores",
    participantsLabelRegex: /formador|particip/i,
  };

  const formadoresParvularia = getProgramaFormadores(
    matchPrograma("Pedagogía en Educación Parvularia")
  );
  const universidadesParvularia =
    getUniversidadesFrecuencia(formadoresParvularia);

  const formadoresBasica = getProgramaFormadores(
    matchPrograma("Pedagogía en Educación Básica")
  );
  const universidadesBasica = getUniversidadesFrecuencia(formadoresBasica);

  const formadoresMedia = getProgramaFormadores(
    matchPrograma("Pedagogía en Educación Media")
  );
  const universidadesMedia = getUniversidadesFrecuencia(formadoresMedia);

  const formadoresDiferencial = getProgramaFormadores(
    matchPrograma("Pedagogía en Educación Diferencial")
  );
  const universidadesDiferencial =
    getUniversidadesFrecuencia(formadoresDiferencial);

  const formadoresFormacionPedagogica = getProgramaFormadores(
    matchPrograma("Formación pedagógica", "Formación Pedagógica")
  );
  const universidadesFormacionPedagogica = getUniversidadesFrecuencia(
    formadoresFormacionPedagogica
  );

  const formadoresPostgrado = getProgramaFormadores(matchPrograma("Postgrado"));
  const universidadesPostgrado = getUniversidadesFrecuencia(formadoresPostgrado);

  const formadoresOtrosProgramas = getProgramaFormadores(
    matchPrograma("Otros Programas", "Otros programas")
  );
  const universidadesOtrosProgramas = getUniversidadesFrecuencia(
    formadoresOtrosProgramas
  );

  const sinInfoProgramaPredicate = (x) =>
    x.id === "—" ||
    x.id === "-" ||
    normalizeText(x.id) === "sin informacion" ||
    normalizeText(x.id) === "sin información";

  const formadoresSinInfoPrograma = getProgramaFormadores(
    sinInfoProgramaPredicate
  );
  const universidadesSinInfoPrograma = getUniversidadesFrecuencia(
    formadoresSinInfoPrograma
  );

  return (
    <PageGuard
      title="FORMACIÓN"
      subtitle="Detalle por años de experiencia, grado, título y programas"
    >
      <Box m="20px" ml="5px" pb="100px">
        {/* =========================
              NIVELES
          ========================= */}
        <Box sx={{ height: 42, display: "flex", alignItems: "center", mt: 2 }}>
          <Typography
            variant="h4"
            fontWeight={600}
            color={colors.primary[100]}
            sx={{ m: 0, fontFamily: "inherit" }}
          >
            AÑOS DE EXPERIENCIA COMO FORMADOR/A
          </Typography>
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "repeat(1, minmax(0, 1fr))",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
          }}
          gap={{ xs: "10px", md: "20px" }}
        >
          <TargetDesc
            {...commonCardProps}
            title="Novel"
            value={getCount(niveles, "novel")}
            description={LEVEL_DESCRIPTION.novel}
            items={sortNames(getList(niveles, "novel"))}
            expandedPaddingTop={12}
          />

          <TargetDesc
            {...commonCardProps}
            title="Intermedio"
            value={getCount(niveles, "intermedio")}
            description={LEVEL_DESCRIPTION.intermedio}
            items={sortNames(getList(niveles, "intermedio"))}
            expandedPaddingTop={12}
          />

          <TargetDesc
            {...commonCardProps}
            title="Experto"
            value={getCount(niveles, "experto")}
            description={LEVEL_DESCRIPTION.experto}
            items={sortNames(getList(niveles, "experto"))}
            expandedPaddingTop={12}
          />

          <TargetDesc
            {...commonCardProps}
            title="Sin información"
            value={getCount(
              niveles,
              (x) => x.id === "—" || x.id === "Sin información"
            )}
            description=""
            items={sortNames(
              getList(
                niveles,
                (x) => x.id === "—" || x.id === "Sin información"
              )
            )}
            expandedPaddingTop={12}
          />
        </Box>

        {/* =========================
              GRADO
          ========================= */}
        <Box sx={{ height: 42, display: "flex", alignItems: "center", mt: 4 }}>
          <Typography
            variant="h4"
            fontWeight={600}
            color={colors.primary[100]}
            sx={{ m: 0, fontFamily: "inherit" }}
          >
            GRADO ACADÉMICO
          </Typography>
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "repeat(1, minmax(0, 1fr))",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
          }}
          gap={{ xs: "10px", md: "20px" }}
        >
          <TargetDesc
            {...commonCardProps}
            title="Licenciatura"
            value={getCount(grado, "Licenciatura")}
            description=""
            items={sortNames(getList(grado, "Licenciatura"))}
            expandedPaddingTop={12}
          />

          <TargetDesc
            {...commonCardProps}
            title="Magíster"
            value={getCount(grado, "Magíster")}
            description=""
            items={sortNames(getList(grado, "Magíster"))}
            expandedPaddingTop={12}
          />

          <TargetDesc
            {...commonCardProps}
            title="Doctorado"
            value={getCount(grado, "Doctorado")}
            description=""
            items={sortNames(getList(grado, "Doctorado"))}
            expandedPaddingTop={12}
          />

          <TargetDesc
            {...commonCardProps}
            title="Sin información"
            value={getCount(grado, "—")}
            description=""
            items={sortNames(getList(grado, "—"))}
            expandedPaddingTop={12}
          />
        </Box>

        {/* =========================
              TÍTULO PROFESIONAL
          ========================= */}
        <Box sx={{ height: 42, display: "flex", alignItems: "center", mt: 4 }}>
          <Typography
            variant="h4"
            fontWeight={600}
            color={colors.primary[100]}
            sx={{ m: 0, fontFamily: "inherit" }}
          >
            TÍTULO PROFESIONAL
          </Typography>
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "repeat(1, minmax(0, 1fr))",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
          }}
          gap={{ xs: "10px", md: "20px" }}
        >
          <TargetDesc
            {...commonCardProps}
            title="Educador/a de Párvulos"
            value={getCount(titulo, "Educador/a de Párvulos")}
            description=""
            items={sortNames(getList(titulo, "Educador/a de Párvulos"))}
            expandedPaddingTop={12}
          />

          <TargetDesc
            {...commonCardProps}
            title="Profesor/a de Educación Básica"
            value={getCount(titulo, "Profesor/a de Educación Básica")}
            description=""
            items={sortNames(getList(titulo, "Profesor/a de Educación Básica"))}
            expandedPaddingTop={12}
          />

          <TargetDesc
            {...commonCardProps}
            title="Profesor/a de Educación Media"
            value={getCount(titulo, "Profesor/a de Educación Media")}
            description=""
            items={sortNames(getList(titulo, "Profesor/a de Educación Media"))}
            expandedPaddingTop={12}
          />

          <TargetDesc
            {...commonCardProps}
            title="Otro Título"
            value={getCount(titulo, "Otro")}
            description=""
            items={sortNames(getList(titulo, "Otro"))}
            expandedPaddingTop={12}
          />

          <TargetDesc
            {...commonCardProps}
            title="Sin información"
            value={getCount(
              titulo,
              (x) => x.id === "—" || x.id === "Sin información"
            )}
            description=""
            items={sortNames(
              getList(
                titulo,
                (x) => x.id === "—" || x.id === "Sin información"
              )
            )}
            expandedPaddingTop={12}
          />
        </Box>

        {/* =========================
              PROGRAMAS DONDE IMPARTEN
          ========================= */}
        <Box sx={{ height: 42, display: "flex", alignItems: "center", mt: 4 }}>
          <Typography
            variant="h4"
            fontWeight={600}
            color={colors.primary[100]}
            sx={{ m: 0, fontFamily: "inherit" }}
          >
            PROGRAMAS Y CARRERAS DONDE IMPARTEN CLASES
          </Typography>
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "repeat(1, minmax(0, 1fr))",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
          }}
          gap={{ xs: "10px", md: "20px" }}
        >
          <TargetMet
            {...programCardProps}
            title="Pedagogía en Educación Parvularia"
            value={getCount(
              programa,
              matchPrograma("Pedagogía en Educación Parvularia")
            )}
            stats={[
              {
                key: "formadores",
                label: {
                  singular: "Formador/a",
                  plural: "Formadores/as",
                },
                value: formadoresParvularia.length,
                items: formadoresParvularia,
              },
              {
                key: "universidades",
                label: {
                  singular: "Universidad",
                  plural: "Universidades",
                },
                value: universidadesParvularia.length,
                items: universidadesParvularia,
              },
            ]}
          />

          <TargetMet
            {...programCardProps}
            title="Pedagogía en Educación Básica"
            value={getCount(
              programa,
              matchPrograma("Pedagogía en Educación Básica")
            )}
            stats={[
              {
                key: "formadores",
                label: {
                  singular: "Formador/a",
                  plural: "Formadores/as",
                },
                value: formadoresBasica.length,
                items: formadoresBasica,
              },
              {
                key: "universidades",
                label: {
                  singular: "Universidad",
                  plural: "Universidades",
                },
                value: universidadesBasica.length,
                items: universidadesBasica,
              },
            ]}
          />

          <TargetMet
            {...programCardProps}
            title="Pedagogía en Educación Media"
            value={getCount(
              programa,
              matchPrograma("Pedagogía en Educación Media")
            )}
            stats={[
              {
                key: "formadores",
                label: {
                  singular: "Formador/a",
                  plural: "Formadores/as",
                },
                value: formadoresMedia.length,
                items: formadoresMedia,
              },
              {
                key: "universidades",
                label: {
                  singular: "Universidad",
                  plural: "Universidades",
                },
                value: universidadesMedia.length,
                items: universidadesMedia,
              },
            ]}
          />

          <TargetMet
            {...programCardProps}
            title="Pedagogía en Educación Diferencial"
            value={getCount(
              programa,
              matchPrograma("Pedagogía en Educación Diferencial")
            )}
            stats={[
              {
                key: "formadores",
                label: {
                  singular: "Formador/a",
                  plural: "Formadores/as",
                },
                value: formadoresDiferencial.length,
                items: formadoresDiferencial,
              },
              {
                key: "universidades",
                label: {
                  singular: "Universidad",
                  plural: "Universidades",
                },
                value: universidadesDiferencial.length,
                items: universidadesDiferencial,
              },
            ]}
          />

          <TargetMet
            {...programCardProps}
            title="Formación pedagógica"
            value={getCount(
              programa,
              matchPrograma("Formación pedagógica", "Formación Pedagógica")
            )}
            stats={[
              {
                key: "formadores",
                label: {
                  singular: "Formador/a",
                  plural: "Formadores/as",
                },
                value: formadoresFormacionPedagogica.length,
                items: formadoresFormacionPedagogica,
              },
              {
                key: "universidades",
                label: {
                  singular: "Universidad",
                  plural: "Universidades",
                },
                value: universidadesFormacionPedagogica.length,
                items: universidadesFormacionPedagogica,
              },
            ]}
          />

          <TargetMet
            {...programCardProps}
            title="Postgrado"
            value={getCount(programa, matchPrograma("Postgrado"))}
            stats={[
              {
                key: "formadores",
                label: {
                  singular: "Formador/a",
                  plural: "Formadores/as",
                },
                value: formadoresPostgrado.length,
                items: formadoresPostgrado,
              },
              {
                key: "universidades",
                label: {
                  singular: "Universidad",
                  plural: "Universidades",
                },
                value: universidadesPostgrado.length,
                items: universidadesPostgrado,
              },
            ]}
          />

          <TargetMet
            {...programCardProps}
            title="Otros Programas"
            value={getCount(
              programa,
              matchPrograma("Otros Programas", "Otros programas")
            )}
            stats={[
              {
                key: "formadores",
                label: {
                  singular: "Formador/a",
                  plural: "Formadores/as",
                },
                value: formadoresOtrosProgramas.length,
                items: formadoresOtrosProgramas,
              },
              {
                key: "universidades",
                label: {
                  singular: "Universidad",
                  plural: "Universidades",
                },
                value: universidadesOtrosProgramas.length,
                items: universidadesOtrosProgramas,
              },
            ]}
          />

          <TargetMet
            {...programCardProps}
            title="Sin información"
            value={getCount(programa, sinInfoProgramaPredicate)}
            stats={[
              {
                key: "formadores",
                label: {
                  singular: "Formador/a",
                  plural: "Formadores/as",
                },
                value: formadoresSinInfoPrograma.length,
                items: formadoresSinInfoPrograma,
              },
              {
                key: "universidades",
                label: {
                  singular: "Universidad",
                  plural: "Universidades",
                },
                value: universidadesSinInfoPrograma.length,
                items: universidadesSinInfoPrograma,
              },
            ]}
          />
        </Box>
      </Box>
    </PageGuard>
  );
};

export default Formacion;